import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useI18n } from "@/i18n";
import { AlertTriangle, Lock, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, no-auth view of an ICE summary.
 * Reads :token from the URL, asks for the 6-digit access code, and renders
 * a read-only version of the document. Both pieces are required.
 *
 * This intentionally re-implements the renderer rather than importing the
 * full /ice page so that no authenticated React context (auth, paywall…)
 * leaks into the public flow.
 */
export default function IceSharedView() {
  const { token } = useParams();
  const { t, lang, setLang } = useI18n();
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Persist last language choice for the public view too.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("ks_lang");
      if (saved && saved !== lang) setLang(saved);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code)) {
      setErr({ key: "code_required" });
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ice/share/${token}`, {
        params: { code },
      });
      setData(r.data);
    } catch (e2) {
      const c = e2?.response?.data?.detail?.code;
      setErr({ key: c || "generic" });
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#0d0c0b]"
        data-testid="ice-share-gate"
      >
        <div className="glass-card rounded-md p-6 sm:p-8 max-w-md w-full">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-gold" />
            <h1 className="font-display text-2xl text-gold-light">
              {t("ice_share_gate_title")}
            </h1>
          </div>
          <p className="text-sm text-muted leading-relaxed mb-5">
            {t("ice_share_gate_intro")}
          </p>
          <form onSubmit={submit} className="space-y-3">
            <label className="font-label text-faded text-xs block">
              {t("ice_share_code_label")}
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              pattern="\d{6}"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••"
              className="input-field font-mono text-2xl tracking-[0.4em] text-center"
              data-testid="ice-share-code-input"
            />
            {err && (
              <div
                className="bg-red-500/10 border border-red-500/30 rounded-md p-3 text-xs text-red-200/90 flex gap-2"
                data-testid="ice-share-error"
              >
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{t(`ice_share_err_${err.key}`) || t("ice_share_err_generic")}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="btn-gold w-full justify-center"
              data-testid="ice-share-submit"
            >
              <Eye className="w-4 h-4" /> {loading ? "…" : t("ice_share_open")}
            </button>
          </form>
          <p className="text-[11px] text-faded mt-4 leading-relaxed">
            {t("ice_share_gate_footer")}
          </p>
        </div>
      </div>
    );
  }

  // Once authenticated by the code, render a compact read-only view.
  // We do NOT reuse the editor (no input fields needed) and we strip
  // any potential ambiguous content.
  return (
    <div className="min-h-screen px-4 py-6 sm:py-10 bg-[#0d0c0b]" data-testid="ice-shared-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-amber-500/5 border border-amber-500/40 rounded-md p-4 flex gap-3 text-xs text-amber-100/90">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
          <span>{t("ice_legal_notice")}</span>
        </div>

        <div className="glass-card rounded-md p-5">
          <div className="font-display text-2xl text-gold-light mb-1">
            {data.owner?.name || data.owner?.email}
          </div>
          <div className="text-xs text-faded">
            {t("ice_share_viewing_as", { name: data.share_meta?.executor_name || "" }) ||
              `${t("ice_share_viewing")}: ${data.share_meta?.executor_name || ""}`}
          </div>
        </div>

        {/* We use <details> tags to keep each section collapsible for a
            long, scannable read on a phone. */}
        {sectionsToShow(data).map(({ key, title, lines }) =>
          lines.length === 0 ? null : (
            <details
              key={key}
              open
              className="glass-card rounded-md p-4"
              data-testid={`shared-${key}`}
            >
              <summary className="font-display text-lg text-gold-light cursor-pointer">
                {title}
              </summary>
              <div className="mt-3 space-y-1.5 text-sm text-gold-light">
                {lines.map((l, i) => (
                  <div key={i} className="whitespace-pre-wrap">{l}</div>
                ))}
              </div>
            </details>
          )
        )}
      </div>
    </div>
  );

  /** Builds a flat ordered set of rendering blocks from the summary payload. */
  function sectionsToShow(d) {
    const out = [];
    const push = (key, title, lines) => out.push({ key, title, lines: lines.filter(Boolean) });
    const p = d.profile || {};

    // Personal
    push("personal", t("ice_sec_personal"), [
      p.personal?.full_name,
      p.personal?.id_number,
      p.personal?.phone_home,
      p.personal?.phone_work,
      p.personal?.email,
      p.personal?.residence,
    ]);
    // Marital
    push("marital", t("ice_sec_marital"), [
      p.marital?.status,
      p.marital?.spouse_name,
      p.marital?.spouse_phone,
      p.marital?.spouse_email,
    ]);
    // Will & Executor
    const w = d.will || {};
    push("will", t("ice_will_title"), [
      w.has_will === "yes" ? `${t("ice_will_title")}: ${w.will_location || ""}` : null,
      w.has_will === "no" ? t("ice_will_missing") : null,
      w.executor_name ? `${t("will_executor_section")}: ${w.executor_name}` : null,
      w.executor_phone,
      w.executor_email,
    ]);
    // Executor instructions
    push("instructions", t("ice_instructions_title"), [d.executor_instructions]);
    // Physical documents
    push(
      "physical",
      t("ice_phys_title"),
      (d.physical_documents || []).map((x) =>
        [x.title, x.location && `(${x.location})`, x.notes].filter(Boolean).join(" — ")
      )
    );
    // Heirs
    push(
      "heirs",
      t("ice_heirs_title"),
      (d.heirs || []).map((h) =>
        [h.name, h.relationship, h.email, h.phone].filter(Boolean).join(" · ")
      )
    );
    // Medical
    push("medical", t("ice_sec_medical"), [
      p.medical?.insurance && `${t("ice_med_insurance")}: ${p.medical.insurance}`,
      p.medical?.blood_type && `${t("ice_med_blood_type")}: ${p.medical.blood_type}`,
      p.medical?.living_will === "yes" && t("ice_med_living_will"),
    ]);
    // Banks
    push(
      "banks",
      t("ice_sec_banks"),
      (p.bank_accounts || []).map((b) => [b.entity, b.reference].filter(Boolean).join(" — "))
    );
    // Investments
    push(
      "investments",
      t("ice_sec_investments"),
      (p.investments || []).map((b) =>
        [b.type, b.platform, b.reference].filter(Boolean).join(" — ")
      )
    );
    // Credentials location
    push("credentials", t("ice_sec_credentials"), [
      p.credentials_location?.where,
      p.credentials_location?.how_to_access,
    ]);
    // Funeral
    push("funeral", t("ice_sec_funeral"), [p.funeral_wishes]);
    // Notes
    push("notes", t("ice_sec_notes"), [p.additional_notes]);
    return out;
  }
}
